// Suppliers → <supplier> → Discount sheet.
//
// The retailer's discount sheet from this supplier, row for row as the
// supplier writes it, plus the retailer's own drilling price per hole. Backed
// by migration 0094:
//   GET    /api/portal/supplier-rates?supplier=<uuid>
//   POST   /api/portal/supplier-rates            PUT/DELETE /api/portal/supplier-rates/:id
//   POST   /api/portal/supplier-rates/import     { mode: 'preview' | 'apply' }
//   PUT    /api/portal/supplier-terms            { drillingChargePerHole }
//
// Nothing in here knows a supplier by name. A new supplier is a new sheet
// imported through the same screen: the CSV columns are guessed from the
// header and confirmed by the retailer (sheet-csv.ts).
//
// These rates PRE-FILL Disc % when a line is added in the Quote Builder
// (quote-builder/discount-rates.ts). Drilling is never discounted, which is
// why it is its own field and not a row in the table.

import { confirmDialog, createModal, iconMarkup } from "@tkos/shared-ui";
import { ApiError } from "@tkos/api-client";
import type { PageContext } from "../../shell/types";
import { SHEET_FIELDS, guessColumns, parseCsv, rowsFromCsv, type SheetField, type SheetRow } from "./sheet-csv";

export interface SheetRateRow {
  id: string;
  discountCode: string | null;
  description: string;
  productType: string | null;
  discountPercent: number;
  validFrom: string | null;
  validUntil: string | null;
  matchNames: string[];
  source: string | null;
  active: boolean;
  matches: number;
}
interface Drilling { perHole: number | null; source: "retailer" | "supplier" | null; supplierDefault: number | null }
interface SheetResp {
  ok: boolean; today: string; rates: SheetRateRow[]; drilling: Drilling;
  catalogue: { products: number; coded: number };
}
interface ImportResp {
  ok: boolean; applied: boolean;
  counts: Record<"incoming" | "adds" | "changes" | "unchanged" | "removals" | "rejects" | "duplicates" | "superseded", number>;
  adds: Array<{ line: number; discountCode: string | null; description: string; discountPercent: number }>;
  changes: Array<{ line: number; before: SheetRateRow; after: { discountPercent: number; description: string; validUntil: string | null } }>;
  removals: SheetRateRow[];
  rejects: Array<{ line: number; reason: string }>;
  duplicates: Array<{ line: number; discountCode: string | null; description: string }>;
  superseded: Array<{ discountCode: string | null; description: string; discountPercent: number; validUntil: string;
    supersededBy: { discountCode: string | null; discountPercent: number; validFrom: string } }>;
}

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));
}
function pct(v: number): string { return `${Number.isInteger(v) ? v : v.toFixed(2).replace(/0$/, "")}%`; }
function gbp(v: number): string { return `£${v.toFixed(2)}`; }
function ukDate(iso: string | null): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}
function errText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    // api-client puts the response text in `message`; ours is {ok, error}.
    try {
      const body = JSON.parse(err.message) as { error?: string };
      if (body.error) return body.error;
    } catch { /* not JSON */ }
    return `${fallback} (HTTP ${err.status})`;
  }
  return fallback;
}

export interface SheetHost {
  supplierId: string;
  supplierName: string;
  ctx: PageContext;
  signal: AbortSignal;
}

export function mountDiscountSheet(host: HTMLElement, opts: SheetHost): { reload: () => Promise<void> } {
  const { supplierId, supplierName, ctx, signal } = opts;
  let data: SheetResp | null = null;
  let filter = "";

  host.innerHTML = `
    <div class="sds">
      <section class="sds-drill" aria-labelledby="sds-drill-${esc(supplierId)}">
        <div class="sds-drill__text">
          <h3 id="sds-drill-${esc(supplierId)}">Hinge drilling</h3>
          <p>Priced per hole on its own. Never discounted, whatever the range.</p>
        </div>
        <label class="sds-drill__field">
          <span class="sds-drill__prefix">£</span>
          <input type="number" inputmode="decimal" min="0" step="0.01" class="sds-drill__input" data-role="drill"
            aria-label="Your drilling price per hole from ${esc(supplierName)}" />
          <span class="sds-drill__unit">per hole</span>
        </label>
        <p class="sds-drill__status" data-role="drill-status" aria-live="polite"></p>
      </section>
      <div class="sup-matrix-tools sds-tools">
        <input type="search" class="sup-matrix-search" data-role="filter" placeholder="Filter by code or range…" aria-label="Filter discount rows" />
        <button type="button" class="sds-btn" data-act="add">${iconMarkup("plus", { size: "sm" })}<span>Add row</span></button>
        <button type="button" class="sds-btn sds-btn--primary" data-act="import">${iconMarkup("upload", { size: "sm" })}<span>Import sheet</span></button>
        <input type="file" accept=".csv,text/csv" data-role="file" hidden />
      </div>
      <p class="sds-summary" data-role="summary"></p>
      <div class="sup-matrix-wrap" data-role="table"><div class="sup-matrix-loading">Loading discount sheet…</div></div>
    </div>`;

  const q = <T extends HTMLElement>(sel: string): T => host.querySelector<T>(sel)!;
  const tableEl = q<HTMLDivElement>("[data-role='table']");
  const summaryEl = q<HTMLParagraphElement>("[data-role='summary']");
  const drillInput = q<HTMLInputElement>("[data-role='drill']");
  const drillStatus = q<HTMLParagraphElement>("[data-role='drill-status']");
  const fileInput = q<HTMLInputElement>("[data-role='file']");

  // ── drilling ──────────────────────────────────────────────────────────────
  function paintDrilling(d: Drilling): void {
    // Update in place: never repaint the panel from a text input's change,
    // or a click that caused the blur is swallowed (QB 09-16 lesson).
    if (document.activeElement !== drillInput) drillInput.value = d.source === "retailer" && d.perHole != null ? d.perHole.toFixed(2) : "";
    drillInput.placeholder = d.supplierDefault != null ? d.supplierDefault.toFixed(2) : "0.00";
    drillStatus.textContent = d.source === "retailer"
      ? `Your price. ${d.supplierDefault != null ? `Supplier default is ${gbp(d.supplierDefault)}; clear the box to use it.` : ""}`
      : d.supplierDefault != null
        ? `Using ${supplierName}'s default, ${gbp(d.supplierDefault)}. Type your own price to replace it.`
        : "No drilling price set. Drilled doors will not get a drilling line until you add one.";
  }

  async function saveDrilling(): Promise<void> {
    const raw = drillInput.value.trim();
    try {
      const resp = await ctx.api<{ ok: boolean; drilling: Drilling }>("/portal/supplier-terms", {
        method: "PUT", signal,
        body: JSON.stringify({ supplier: supplierId, drillingChargePerHole: raw === "" ? null : Number(raw) }),
      });
      if (data) data.drilling = resp.drilling;
      paintDrilling(resp.drilling);
      ctx.showToast(raw === "" ? "Drilling price reset to the supplier's default" : `Drilling saved: ${gbp(Number(raw))} per hole`, "success");
    } catch (err) {
      if ((err as { name?: string })?.name === "AbortError") return;
      ctx.showToast(errText(err, "Couldn't save the drilling price"), "error");
    }
  }

  // ── table ─────────────────────────────────────────────────────────────────
  function statusTag(r: SheetRateRow, today: string): string {
    if (r.active) return "";
    if (r.validFrom && r.validFrom > today) return `<span class="sds-tag">From ${esc(ukDate(r.validFrom))}</span>`;
    return `<span class="sds-tag">Ended ${esc(ukDate(r.validUntil))}</span>`;
  }

  function paintTable(): void {
    if (!data) return;
    const rows = data.rates;
    const showType = rows.some((r) => r.productType);
    const showDates = rows.some((r) => r.validFrom || r.validUntil);
    const live = rows.filter((r) => r.active);
    const reached = live.reduce((n, r) => n + r.matches, 0);
    const dormant = live.filter((r) => r.matches === 0).length;
    summaryEl.textContent = rows.length === 0 ? "" :
      `${rows.length} rows · ${live.length} in force${rows.length - live.length ? ` · ${rows.length - live.length} not in force` : ""}` +
      ` · reaching ${reached.toLocaleString("en-GB")} of ${data.catalogue.products.toLocaleString("en-GB")} catalogue products` +
      (dormant ? ` · ${dormant} waiting for products` : "");
    if (rows.length === 0) {
      tableEl.innerHTML = `<div class="sup-matrix-empty sds-empty">
        <p><strong>No discount sheet for ${esc(supplierName)} yet.</strong></p>
        <p>Import the CSV your supplier sends you, or add rows by hand. Each row's discount pre-fills Disc % on new quote lines.</p>
      </div>`;
      return;
    }
    const f = filter.toLowerCase();
    tableEl.innerHTML = `
      <table class="sup-matrix sds-table">
        <thead><tr>
          <th>Code</th><th>Range / description</th>${showType ? "<th>Type</th>" : ""}
          <th class="sds-num">Discount</th>${showDates ? "<th>Valid</th>" : ""}
          <th class="sds-num" title="How many products in the catalogue this row reaches">Matches</th><th><span class="sr-only">Actions</span></th>
        </tr></thead>
        <tbody>${rows.map((r) => {
          const hay = `${r.discountCode ?? ""} ${r.description} ${r.productType ?? ""} ${r.matchNames.join(" ")}`.toLowerCase();
          const hidden = f && !hay.includes(f) ? " hidden" : "";
          const also = r.matchNames.length ? `<span class="sds-also">also: ${esc(r.matchNames.join(", "))}</span>` : "";
          const matches = !r.active ? `<span class="sup-muted">—</span>`
            : r.matches === 0 ? `<span class="sds-dormant" title="No catalogue product matches this row yet. It applies as soon as one does.">0</span>`
            : r.matches.toLocaleString("en-GB");
          return `<tr data-id="${esc(r.id)}" class="${r.active ? "" : "is-inactive"}"${hidden} data-hay="${esc(hay)}">
            <td class="sds-code">${esc(r.discountCode ?? "")}</td>
            <td class="sup-mx-name">${esc(r.description)} ${statusTag(r, data!.today)}${also}</td>
            ${showType ? `<td>${esc(r.productType ?? "")}</td>` : ""}
            <td class="sds-num sup-mx-eff">${esc(pct(r.discountPercent))}</td>
            ${showDates ? `<td class="sds-dates">${esc(ukDate(r.validFrom))}${r.validUntil ? ` – ${esc(ukDate(r.validUntil))}` : ""}</td>` : ""}
            <td class="sds-num">${matches}</td>
            <td class="sds-actions">
              <button type="button" class="sds-icon" data-act="edit" data-id="${esc(r.id)}" aria-label="Edit ${esc(r.description)}">${iconMarkup("pencil", { size: "sm" })}</button>
              <button type="button" class="sds-icon" data-act="delete" data-id="${esc(r.id)}" aria-label="Delete ${esc(r.description)}">${iconMarkup("trash", { size: "sm" })}</button>
            </td>
          </tr>`;
        }).join("")}</tbody>
      </table>`;
  }

  async function reload(): Promise<void> {
    try {
      const resp = await ctx.api<SheetResp>(`/portal/supplier-rates?supplier=${encodeURIComponent(supplierId)}`, { signal });
      data = resp;
      paintDrilling(resp.drilling);
      paintTable();
    } catch (err) {
      if ((err as { name?: string })?.name === "AbortError") return;
      tableEl.innerHTML = `<div class="sup-matrix-error">Couldn't load the discount sheet. <button type="button" class="sup-link" data-act="retry">Retry</button></div>`;
    }
  }

  // ── add / edit ────────────────────────────────────────────────────────────
  function openEditor(row: SheetRateRow | null): void {
    let form: HTMLFormElement | null = null;
    const modal = createModal({
      title: row ? "Edit discount row" : `Add a ${supplierName} discount row`,
      size: "md",
      content: (body) => {
        body.innerHTML = `
          <form class="sds-form" novalidate>
            <label><span>Range / description</span><input name="description" required maxlength="200" value="${esc(row?.description ?? "")}" /></label>
            <div class="sds-form__row">
              <label><span>Discount %</span><input name="discountPercent" type="number" min="0" max="100" step="0.01" required value="${row ? row.discountPercent : ""}" /></label>
              <label><span>Discount code <em>optional</em></span><input name="discountCode" maxlength="40" value="${esc(row?.discountCode ?? "")}" /></label>
            </div>
            <label><span>Product type <em>optional</em></span><input name="productType" maxlength="60" value="${esc(row?.productType ?? "")}" placeholder="Door, Accessory, Fitting…" /></label>
            <div class="sds-form__row">
              <label><span>Valid from <em>optional</em></span><input name="validFrom" type="date" value="${esc(row?.validFrom ?? "")}" /></label>
              <label><span>Valid until <em>optional</em></span><input name="validUntil" type="date" value="${esc(row?.validUntil ?? "")}" /></label>
            </div>
            <label><span>Also matches <em>optional, separate with ;</em></span>
              <input name="matchNames" value="${esc((row?.matchNames ?? []).join("; "))}" placeholder="e.g. Hinges & Plates" /></label>
            <p class="sds-form__hint">A quote line takes this row when its discount code matches, or its range / category is this name.</p>
            <p class="sds-form__error" data-role="err" role="alert" hidden></p>
          </form>`;
        form = body.querySelector("form");
      },
      actions: [
        { label: "Cancel", variant: "ghost" },
        {
          label: row ? "Save" : "Add row", variant: "primary", closeOnClick: false,
          onClick: async () => {
            if (!form) return;
            const fd = new FormData(form);
            const body = {
              supplier: supplierId,
              description: String(fd.get("description") ?? ""),
              discountPercent: String(fd.get("discountPercent") ?? ""),
              discountCode: String(fd.get("discountCode") ?? ""),
              productType: String(fd.get("productType") ?? ""),
              validFrom: String(fd.get("validFrom") ?? ""),
              validUntil: String(fd.get("validUntil") ?? ""),
              matchNames: String(fd.get("matchNames") ?? "").split(";").map((s) => s.trim()).filter(Boolean),
            };
            const errEl = form.querySelector<HTMLElement>("[data-role='err']")!;
            try {
              await ctx.api(row ? `/portal/supplier-rates/${encodeURIComponent(row.id)}` : "/portal/supplier-rates", {
                method: row ? "PUT" : "POST", body: JSON.stringify(body), signal,
              });
              modal.close();
              ctx.showToast(row ? "Discount row saved" : "Discount row added", "success");
              await reload();
            } catch (err) {
              if ((err as { name?: string })?.name === "AbortError") return;
              errEl.textContent = errText(err, "Couldn't save the row");
              errEl.hidden = false;
            }
          },
        },
      ],
      onClose: () => window.setTimeout(() => modal.destroy(), 300),
    });
    modal.open();
  }

  async function remove(row: SheetRateRow): Promise<void> {
    const ok = await confirmDialog(
      `Delete "${row.description}"${row.discountCode ? ` (${row.discountCode})` : ""} at ${pct(row.discountPercent)}? New quote lines for this range will no longer pre-fill a discount from it. Existing quotes are not changed.`,
      { title: "Delete discount row", confirmLabel: "Delete", destructive: true });
    if (!ok) return;
    try {
      await ctx.api(`/portal/supplier-rates/${encodeURIComponent(row.id)}`, { method: "DELETE", signal });
      ctx.showToast("Discount row deleted", "success");
      await reload();
    } catch (err) {
      if ((err as { name?: string })?.name === "AbortError") return;
      ctx.showToast(errText(err, "Couldn't delete the row"), "error");
    }
  }

  // ── import ────────────────────────────────────────────────────────────────
  function openMapping(fileName: string, table: string[][]): void {
    const headers = table[0] ?? [];
    const guess = guessColumns(headers);
    let formEl: HTMLElement | null = null;
    const modal = createModal({
      title: `Import ${supplierName} discount sheet`,
      subtitle: `${fileName} · ${table.length - 1} rows`,
      size: "lg",
      content: (body) => {
        const opt = (f: SheetField) => `<option value="-1">— not in this sheet —</option>` +
          headers.map((h, i) => `<option value="${i}"${guess[f] === i ? " selected" : ""}>${esc(h)}</option>`).join("");
        const sample = table.slice(1, 4);
        body.innerHTML = `
          <div class="sds-map">
            <p>Check which column is which. These were guessed from the headings.</p>
            <div class="sds-map__grid">${SHEET_FIELDS.map((f) => `
              <label><span>${esc(f.label)}${f.required ? "" : " <em>optional</em>"}</span>
                <select data-field="${f.key}">${opt(f.key)}</select></label>`).join("")}
            </div>
            <div class="sds-map__sample"><table class="sup-matrix">
              <thead><tr>${headers.map((h) => `<th>${esc(h)}</th>`).join("")}</tr></thead>
              <tbody>${sample.map((r) => `<tr>${headers.map((_, i) => `<td>${esc(r[i] ?? "")}</td>`).join("")}</tr>`).join("")}</tbody>
            </table></div>
            <label class="sup-toggle sds-map__replace"><input type="checkbox" data-role="replace" />
              <span>Replace the whole sheet: delete rows I already have that are not in this file</span></label>
            <p class="sds-form__error" data-role="err" role="alert" hidden></p>
          </div>`;
        formEl = body;
      },
      actions: [
        { label: "Cancel", variant: "ghost" },
        {
          label: "Check the import", variant: "primary", closeOnClick: false,
          onClick: async () => {
            if (!formEl) return;
            const cols = {} as Record<SheetField, number>;
            formEl.querySelectorAll<HTMLSelectElement>("select[data-field]").forEach((s) => { cols[s.dataset.field as SheetField] = Number(s.value); });
            const errEl = formEl.querySelector<HTMLElement>("[data-role='err']")!;
            const missing = SHEET_FIELDS.filter((f) => f.required && cols[f.key] < 0).map((f) => f.label);
            if (missing.length) { errEl.textContent = `Choose a column for: ${missing.join(", ")}.`; errEl.hidden = false; return; }
            const replace = formEl.querySelector<HTMLInputElement>("[data-role='replace']")!.checked;
            const rows = rowsFromCsv(table, cols);
            try {
              const plan = await runImport(rows, replace, fileName, "preview");
              modal.close();
              openPreview(fileName, rows, replace, plan);
            } catch (err) {
              if ((err as { name?: string })?.name === "AbortError") return;
              errEl.textContent = errText(err, "Couldn't check the import");
              errEl.hidden = false;
            }
          },
        },
      ],
      onClose: () => window.setTimeout(() => modal.destroy(), 300),
    });
    modal.open();
  }

  function runImport(rows: SheetRow[], replace: boolean, fileName: string, mode: "preview" | "apply"): Promise<ImportResp> {
    return ctx.api<ImportResp>("/portal/supplier-rates/import", {
      method: "POST", signal,
      body: JSON.stringify({ supplier: supplierId, rows, replace, mode, source: fileName }),
    });
  }

  function openPreview(fileName: string, rows: SheetRow[], replace: boolean, plan: ImportResp): void {
    const c = plan.counts;
    const writes = c.adds + c.changes + c.removals;
    const list = (title: string, items: string[], tone = "") => items.length === 0 ? "" : `
      <details class="sds-plan__group ${tone}"${items.length <= 12 ? " open" : ""}>
        <summary>${esc(title)} <span>${items.length}</span></summary>
        <ul>${items.slice(0, 300).map((i) => `<li>${i}</li>`).join("")}${items.length > 300 ? `<li>… and ${items.length - 300} more</li>` : ""}</ul>
      </details>`;
    const modal = createModal({
      title: writes ? `Import ${writes} change${writes === 1 ? "" : "s"}?` : "Nothing to change",
      subtitle: `${fileName} · ${c.incoming} rows read`,
      size: "lg",
      content: (body) => {
        body.innerHTML = `
          <div class="sds-plan">
            <div class="sds-plan__counts">
              <div><strong>${c.adds}</strong><span>new</span></div>
              <div><strong>${c.changes}</strong><span>changed</span></div>
              <div><strong>${c.unchanged}</strong><span>unchanged</span></div>
              ${replace ? `<div><strong>${c.removals}</strong><span>to delete</span></div>` : ""}
              <div class="${c.rejects + c.duplicates ? "is-warn" : ""}"><strong>${c.rejects + c.duplicates}</strong><span>skipped</span></div>
            </div>
            ${list("Superseded — kept as history, not used for quotes", plan.superseded.map((s) =>
              `${esc(s.description)} ${esc(s.discountCode ?? "")} at ${esc(pct(s.discountPercent))}: ends ${esc(ukDate(s.validUntil))}, replaced by ${esc(pct(s.supersededBy.discountPercent))} from ${esc(ukDate(s.supersededBy.validFrom))}`), "is-info")}
            ${list("Skipped", [
              ...plan.rejects.map((r) => `Line ${r.line + 1}: ${esc(r.reason)}`),
              ...plan.duplicates.map((d) => `Line ${d.line + 1}: ${esc(d.discountCode ?? d.description)} appears twice — the first is kept`),
            ], "is-warn")}
            ${list("Changed", plan.changes.map((ch) =>
              `${esc(ch.before.discountCode ?? "")} ${esc(ch.before.description)}: ${esc(pct(ch.before.discountPercent))} → ${esc(pct(ch.after.discountPercent))}`))}
            ${list("To delete", plan.removals.map((r) => `${esc(r.discountCode ?? "")} ${esc(r.description)} (${esc(pct(r.discountPercent))})`), "is-warn")}
            ${list("New", plan.adds.map((a) => `${esc(a.discountCode ?? "")} ${esc(a.description)} — ${esc(pct(a.discountPercent))}`))}
            <p class="sds-form__hint">Existing quotes are not touched. The new rates pre-fill Disc % on lines added from now on.</p>
          </div>`;
      },
      actions: writes ? [
        { label: "Back", variant: "ghost" },
        {
          label: `Import ${writes} change${writes === 1 ? "" : "s"}`, variant: "primary",
          onClick: async () => {
            try {
              const done = await runImport(rows, replace, fileName, "apply");
              ctx.showToast(`Discount sheet imported: ${done.counts.adds} new, ${done.counts.changes} changed${replace ? `, ${done.counts.removals} deleted` : ""}`, "success");
              await reload();
            } catch (err) {
              if ((err as { name?: string })?.name === "AbortError") return;
              ctx.showToast(errText(err, "The import failed; nothing was changed"), "error");
            }
          },
        },
      ] : [{ label: "Close", variant: "primary" }],
      onClose: () => window.setTimeout(() => modal.destroy(), 300),
    });
    modal.open();
  }

  async function onFile(): Promise<void> {
    const file = fileInput.files?.[0];
    fileInput.value = "";
    if (!file) return;
    if (file.size > 2_000_000) { ctx.showToast("That file is too large for a discount sheet (2 MB max)", "error"); return; }
    const table = parseCsv(await file.text());
    if (table.length < 2) { ctx.showToast("That file has no rows under its heading", "error"); return; }
    openMapping(file.name, table);
  }

  // ── events ────────────────────────────────────────────────────────────────
  host.addEventListener("click", (ev) => {
    const t = (ev.target as HTMLElement).closest<HTMLElement>("[data-act]");
    if (!t) return;
    const act = t.dataset.act;
    const row = data?.rates.find((r) => r.id === t.dataset.id) ?? null;
    if (act === "add") openEditor(null);
    else if (act === "import") fileInput.click();
    else if (act === "edit" && row) openEditor(row);
    else if (act === "delete" && row) void remove(row);
    else if (act === "retry") void reload();
  });
  host.addEventListener("input", (ev) => {
    const t = ev.target as HTMLElement;
    if (!(t instanceof HTMLInputElement) || t.dataset.role !== "filter") return;
    filter = t.value.trim();
    const f = filter.toLowerCase();
    tableEl.querySelectorAll<HTMLTableRowElement>("tbody tr").forEach((tr) => {
      tr.hidden = f !== "" && !(tr.dataset.hay ?? "").includes(f);
    });
  });
  drillInput.addEventListener("change", () => { void saveDrilling(); });
  fileInput.addEventListener("change", () => { void onFile(); });

  void reload();
  return { reload };
}
